import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, Sliders, Package } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { LoadingScreen } from "@/components/common";

const WEIGHT_KEYS = ["location", "category", "manpower", "experience", "rating", "compliance", "response", "price"];

export default function AdminSettings() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <DashboardShell menu={adminMenu} title="Settings"><LoadingScreen /></DashboardShell>;

  const save = async () => {
    try { await api.put("/admin/config", { commission_pct: Number(cfg.commission_pct), match_weights: cfg.match_weights }); toast.success("Settings saved"); }
    catch (e) { if (!e.isAuthError) toast.error("Could not save settings"); }
  };
  const setWeight = (k, v) => setCfg({ ...cfg, match_weights: { ...cfg.match_weights, [k]: v } });
  const total = WEIGHT_KEYS.reduce((s, k) => s + (cfg.match_weights?.[k] || 0), 0);

  return (
    <DashboardShell menu={adminMenu} title="Marketplace Settings">
      <PageHeader title="Revenue & Matching Configuration" subtitle="Configure commission and the transparent AI matching weights." />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-white border border-navy-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4"><Settings size={18} className="text-brand" /><h3 className="font-heading font-semibold text-navy-900">Commission & Plans</h3></div>
          <Label className="text-xs">Commission (% of contract value)</Label>
          <Input type="number" value={cfg.commission_pct} onChange={(e) => setCfg({ ...cfg, commission_pct: e.target.value })} className="mt-1 w-40" data-testid="commission-input" />
          <div className="mt-5 pt-4 border-t border-navy-100">
            <div className="flex items-center gap-2 text-sm text-navy-600"><Package size={16} className="text-brand" /> Subscription packages are managed in the Package Master.</div>
            <Button variant="outline" className="mt-3 border-navy-300" onClick={() => navigate("/admin/packages")} data-testid="manage-packages-btn">Manage Packages →</Button>
          </div>
        </div>

        <div className="bg-white border border-navy-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Sliders size={18} className="text-brand" /><h3 className="font-heading font-semibold text-navy-900">AI Matching Weights</h3></div>
            <span className={`text-sm font-semibold ${total === 100 ? "text-emerald-600" : "text-amber-600"}`}>Total: {total}</span>
          </div>
          <p className="text-xs text-navy-500 mb-4">Ranking is transparent and fully configurable. Weights ideally sum to 100.</p>
          <div className="space-y-4">
            {WEIGHT_KEYS.map((k) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span className="capitalize text-navy-700">{k}</span><span className="font-semibold text-navy-900">{cfg.match_weights?.[k] || 0}</span></div>
                <Slider value={[cfg.match_weights?.[k] || 0]} max={40} step={1} onValueChange={([v]) => setWeight(k, v)} data-testid={`weight-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <Button className="mt-6 bg-brand hover:bg-brand-600 h-11 px-8" onClick={save} data-testid="save-settings-btn">Save Settings</Button>
    </DashboardShell>
  );
}
